import {
  forwardRef,
  InputHTMLAttributes,
  ReactNode,
  KeyboardEvent,
  useState,
  useEffect,
  useRef,
} from 'react';
import { cn, FONT_SIZES, HEIGHTS } from '@/lib/utils';
import { ComponentSize } from '@/interfaces';
import { CloseSmallIcon, ViewIcon, ViewOffIcon } from '@wisdesign/lsicon';
import { Button } from './button';

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'prefix' | 'suffix'> {
  size?: ComponentSize;
  prefix?: ReactNode;
  suffix?: ReactNode;
  suffixClassName?: string;
  inputClassName?: string;
  allowClear?: boolean;
  hidePasswordToggle?: boolean;
  bigPadding?: boolean;
  onClear?: () => void;
  onPressEnter?: (e: KeyboardEvent<HTMLInputElement>) => void;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type: initialType,
      size = 'default',
      prefix,
      suffix,
      suffixClassName,
      inputClassName,
      allowClear,
      hidePasswordToggle = false,
      bigPadding = false,
      onClear,
      onPressEnter,
      onKeyDown,
      value,
      onChange,
      ...props
    },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [showClear, setShowClear] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const isControlled = value !== undefined;
    const isPassword = initialType === 'password';
    const type = isPassword ? (showPassword ? 'text' : 'password') : initialType;

    const updateClearButtonVisibility = () => {
      const inputValue = isControlled ? value : inputRef.current?.value;
      setShowClear(Boolean(inputValue) && Boolean(allowClear));
    };

    useEffect(() => {
      updateClearButtonVisibility();
    }, [value, allowClear]);

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && onPressEnter) {
        onPressEnter(e);
      }
      onKeyDown?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
      if (!isControlled) {
        updateClearButtonVisibility();
      }
    };

    const handleClear = () => {
      onClear?.();

      if (!isControlled && inputRef.current) {
        inputRef.current.value = '';
        const changeEvent = new Event('change', { bubbles: true });
        inputRef.current.dispatchEvent(changeEvent);
      }

      if (isControlled && onChange) {
        const changeEvent = {
          target: { value: '' },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(changeEvent);
      }

      updateClearButtonVisibility();
    };

    const passwordToggleButton = isPassword && !hidePasswordToggle && (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="hover:bg-transparent"
        onClick={() => setShowPassword((prev) => !prev)}
      >
        {showPassword ? <ViewIcon className="h-4 w-4" /> : <ViewOffIcon className="h-4 w-4" />}
      </Button>
    );

    return (
      <div className={cn('relative flex items-center', className)}>
        {prefix && <div className="absolute left-[8px]">{prefix}</div>}
        <input
          type={type}
          className={cn(
            'flex w-full rounded-md border border-input bg-box px transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-helper focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
            prefix && (bigPadding ? 'pl-[30px]' : 'pl-[26px]'),
            ((suffix && !showClear) || (!suffix && showClear)) && 'pr-[26px]',
            suffix && showClear && 'pr-[46px]',
            isPassword && !hidePasswordToggle && 'pr-[36px]',
            HEIGHTS[size],
            FONT_SIZES[size],
            inputClassName,
          )}
          ref={(node) => {
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
            inputRef.current = node;
          }}
          placeholder="请输入"
          onKeyDown={handleKeyDown}
          value={value}
          onChange={handleChange}
          {...props}
        />
        <div className={cn('absolute right-[8px] flex items-center gap-1', suffixClassName)}>
          {showClear && (
            <CloseSmallIcon
              className="cursor-pointer text-03 hover:text-01"
              onClick={handleClear}
            />
          )}
          {passwordToggleButton}
          {suffix}
        </div>
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
